// Self Assign popup body — a custom-body popup (see DynamicPopup's
// config.component escape hatch). It restores the old Jobs SelfAssignPopup
// behaviour: GET /self-assign decides between a simple "Are you sure you want
// to assign this job?" confirmation (no submissions yet) and a "Team Members
// Allocated" table + warning when other users are already working the job.
// The body owns its own No/Yes footer and submit (selfAssignJob).
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, Table, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { getSelfAssignInfo, getJobAssignedRecruiters, selfAssignJob } from '../../../../services/popupApi';

const columns = [
  {
    title: 'Name',
    key: 'name',
    render: (_, record) =>
      record.name ||
      `${record.first_name ?? ''} ${record.last_name ?? ''}`.trim() ||
      record.user_name ||
      'Assigned user',
  },
  { title: 'Role', dataIndex: 'role_name', key: 'role', render: (v) => v || '-' },
  { title: 'Reporting', dataIndex: 'reporting_name', key: 'reporting', render: (v) => v || '-' },
];

export default function SelfAssignBody({ selectedIds, onClose }) {
  const jobId = selectedIds?.[0];
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState({ assignedUsers: [], totalSubmissions: 0, message: '' });
  // The already-assigned recruiters — available regardless of submission count
  // (GET /pay-details), unlike self-assign's assignedUsers which the backend
  // only returns once the job HAS submissions. This is what lets the popup
  // always list everyone already on the job.
  const [recruiters, setRecruiters] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getSelfAssignInfo(jobId).catch((error) => {
        if (!cancelled) message.error(error?.message ?? 'Failed to load self-assign details');
        return { assignedUsers: [], totalSubmissions: 0, message: '' };
      }),
      getJobAssignedRecruiters(jobId).catch(() => ({ recruitersData: [] })),
    ])
      .then(([selfInfo, payDetails]) => {
        if (cancelled) return;
        setInfo(selfInfo);
        setRecruiters(Array.isArray(payDetails?.recruitersData) ? payDetails.recruitersData : []);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleYes = async () => {
    setSubmitting(true);
    try {
      await selfAssignJob({ jobIds: [jobId] });
      message.success('Job assigned successfully');
      onClose?.({ success: true });
    } catch (error) {
      message.error(error?.message ?? 'Failed to assign job');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin size="large" /></div>;
  }

  // Prefer the richer self-assign rows (Name / Role / Reporting) when present;
  // otherwise fall back to the pay-details recruiters so the "already assigned"
  // list shows even when the job has no submissions yet.
  const payDetailRows = recruiters.map((r) => ({
    user_id: r.recruiterId ?? r.id ?? r.user_id,
    name: r.name ?? '',
    role_name: r.role_name ?? r.roleName ?? '',
    reporting_name: r.reporting_name ?? r.reportingName ?? '',
  }));
  const payDetailRowsById = new Map(
    payDetailRows.map((row) => [String(row.user_id ?? ''), row]),
  );
  const rows = (info.assignedUsers?.length ?? 0) > 0
    ? info.assignedUsers.map((user) => {
        const id = user.user_id ?? user.userId ?? user.recruiterId ?? user.id;
        const directory = payDetailRowsById.get(String(id ?? '')) ?? {};
        return {
          ...user,
          user_id: id,
          name: directory.name || user.name || '',
          role_name: directory.role_name || user.role_name || user.roleName || '',
          reporting_name:
            directory.reporting_name || user.reporting_name || user.reportingName || '',
        };
      })
    : payDetailRows;
  const hasAllocated = rows.length > 0;

  return (
    <div className="self-assign-popup-body">
      {hasAllocated ? (
        <>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Team Members Allocated</div>
          <Table
            columns={columns}
            dataSource={rows}
            pagination={false}
            rowKey={(r) => r.user_id ?? `${r.first_name}-${r.last_name}`}
            size="small"
            className="mb-3 no-header-bg"
            scroll={{ y: 200 }}
          />
          <div
            className="warning-card"
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}
          >
            <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 18 }} />
            <div style={{ fontSize: 13 }}>
              These users are already assigned to this job. Do you still want to assign yourself as
              well?
            </div>
          </div>
        </>
      ) : (
        <div style={{ padding: '8px 0' }}>
          {info.message || 'No one is currently assigned to this job. Do you want to assign yourself?'}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
        <Button size="small" onClick={() => onClose?.()} disabled={submitting}>
          No
        </Button>
        <Button type="primary" size="small" onClick={handleYes} loading={submitting}>
          Yes
        </Button>
      </div>
    </div>
  );
}

SelfAssignBody.propTypes = {
  selectedIds: PropTypes.array,
  onClose: PropTypes.func,
};
